import React, { useEffect, useState } from "react";
import { Target, Plus, Pencil } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../../components/ui/select";
import CatalogTable from "../components/CatalogTable";
import { CatalogLoadingCard, CatalogErrorCard } from "../components/CatalogStateCard";
import EntityFormDialog from "../components/EntityFormDialog";
import StatusBadge from "../components/StatusBadge";
import { useEmailCatalogEntityList } from "../../../hooks/useEmailCatalogEntityList";
import {
  getEmailMarketingCampaigns,
  getEmailMarketingPrograms,
  getEmailMarketingAngles,
  createCampaign,
  updateCampaign,
} from "../../../lib/emailMarketingApi";

// EMAILS-IMPL-007: Campaign authoring. `program_id` is immutable after
// creation (CMP-SPEC-001 §17.1 Historical Integrity; enforced server-side
// by update_campaign_authored never accepting it) -- the edit form shows
// it read-only, never as an input the API would reject. `angle_id` may
// only be FIRST-assigned (None -> a value), never reassigned once set --
// the edit form reflects that directly: a Select while unset, a read-only
// value once set, rather than offering an edit the API will 409.
export const CAMPAIGN_STATUS_VALUES = ["Draft", "Review", "Approved", "Active", "Retired", "Archived"];

const NO_ANGLE = "none";

function emptyForm() {
  return { name: "", status: "Draft", programId: "", angleId: NO_ANGLE };
}

export default function CampaignsPanel() {
  const { items, loading, error, mutating, mutationError, clearMutationError, createItem, updateItem } =
    useEmailCatalogEntityList({
      list: getEmailMarketingCampaigns,
      create: createCampaign,
      update: updateCampaign,
      getId: (row) => row.campaign_id,
    });

  const [programs, setPrograms] = useState([]);
  const [angles, setAngles] = useState([]);
  const [refLoading, setRefLoading] = useState(true);
  const [refError, setRefError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [p, a] = await Promise.all([getEmailMarketingPrograms(), getEmailMarketingAngles()]);
        if (!cancelled) {
          setPrograms(p || []);
          setAngles(a || []);
        }
      } catch (err) {
        if (!cancelled) setRefError("Failed to load Programs/Angles for the Campaign form.");
      } finally {
        if (!cancelled) setRefLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const programName = (id) => programs.find((p) => p.program_id === id)?.name;
  const angleName = (id) => angles.find((a) => a.angle_id === id)?.name;

  const [expandedId, setExpandedId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyForm());
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm());

  const openCreate = () => {
    setCreateForm(emptyForm());
    clearMutationError();
    setCreateOpen(true);
  };

  const submitCreate = async () => {
    try {
      await createItem({
        name: createForm.name.trim(),
        status: createForm.status,
        programId: createForm.programId,
        angleId: createForm.angleId === NO_ANGLE ? null : createForm.angleId,
      });
      setCreateOpen(false);
    } catch {
      // handled via mutationError
    }
  };

  const openEdit = (campaign) => {
    setEditingCampaign(campaign);
    setEditForm({
      name: campaign.name,
      status: campaign.status,
      programId: campaign.program_id,
      angleId: campaign.angle_id || NO_ANGLE,
    });
    clearMutationError();
  };

  const submitEdit = async () => {
    const angleAlreadySet = !!editingCampaign.angle_id;
    try {
      await updateItem(editingCampaign.campaign_id, {
        name: editForm.name.trim() !== editingCampaign.name ? editForm.name.trim() : undefined,
        status: editForm.status !== editingCampaign.status ? editForm.status : undefined,
        // Only ever sent when the Campaign had no Angle yet -- once set,
        // the field is rendered read-only below and never reaches here.
        angleId: !angleAlreadySet && editForm.angleId !== NO_ANGLE ? editForm.angleId : undefined,
      });
      setEditingCampaign(null);
    } catch {
      // handled via mutationError
    }
  };

  if (loading || refLoading) return <CatalogLoadingCard testId="campaigns-loading" />;
  if (error) return <CatalogErrorCard message={error} testId="campaigns-error" />;
  if (refError) return <CatalogErrorCard message={refError} testId="campaigns-ref-error" />;

  const noPrograms = programs.length === 0;

  return (
    <div className="space-y-4" data-testid="campaigns-panel">
      <CatalogTable
        testId="campaigns-table"
        title="Campaigns"
        icon={Target}
        headerAction={
          <Button
            type="button"
            size="sm"
            className="gap-1.5 bg-violet-600 text-white hover:bg-violet-500"
            onClick={openCreate}
            disabled={noPrograms}
            title={noPrograms ? "Create a Program first" : undefined}
            data-testid="new-campaign-button"
          >
            <Plus className="h-4 w-4" /> New Campaign
          </Button>
        }
        rows={items}
        getRowKey={(row) => row.campaign_id}
        onRowClick={(row) => setExpandedId(expandedId === row.campaign_id ? null : row.campaign_id)}
        emptyTitle="No Campaigns yet"
        emptySubtitle={noPrograms ? "Create a Program first, then create a Campaign within it." : 'Create one with "New Campaign" to get started.'}
        columns={[
          {
            key: "name",
            label: "Campaign",
            render: (row) => (
              <div>
                <div className="text-sm font-medium text-slate-100">{row.name}</div>
                <div className="text-xs text-slate-400">{row.campaign_id}</div>
              </div>
            ),
          },
          {
            key: "program",
            label: "Program",
            render: (row) => <span className="text-sm text-slate-300">{programName(row.program_id) || row.program_id}</span>,
          },
          {
            key: "angle",
            label: "Angle",
            render: (row) => (
              <span className="text-sm text-slate-300">{row.angle_id ? angleName(row.angle_id) || row.angle_id : "—"}</span>
            ),
          },
          { key: "status", label: "Status", render: (row) => <StatusBadge status={row.status} testId={`campaign-status-${row.campaign_id}`} /> },
        ]}
      />

      {items
        .filter((row) => row.campaign_id === expandedId)
        .map((row) => (
          <div key={row.campaign_id} className="rounded-lg border border-white/10 bg-white/5 p-4" data-testid={`campaign-detail-${row.campaign_id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
                <Detail label="Campaign ID" value={row.campaign_id} mono />
                <Detail label="Program" value={`${row.program_id}${programName(row.program_id) ? ` — ${programName(row.program_id)}` : ""}`} />
                <Detail label="Angle" value={row.angle_id ? `${row.angle_id}${angleName(row.angle_id) ? ` — ${angleName(row.angle_id)}` : ""}` : "Not yet assigned"} />
                <Detail label="Status" value={row.status} />
                <Detail label="Version" value={row.version || "—"} mono />
                <Detail label="Created" value={row.created_at ? new Date(row.created_at).toLocaleString() : "—"} />
              </div>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="gap-1.5 border-white/10 text-slate-200"
                onClick={() => openEdit(row)}
                data-testid={`edit-campaign-${row.campaign_id}`}
              >
                <Pencil className="h-3.5 w-3.5" /> Edit
              </Button>
            </div>
          </div>
        ))}

      <EntityFormDialog
        testId="create-campaign-dialog"
        open={createOpen}
        onOpenChange={setCreateOpen}
        title="New Campaign"
        description="Campaigns define marketing strategy within a Program."
        onSubmit={submitCreate}
        submitting={mutating}
        error={mutationError}
        submitLabel="Create Campaign"
      >
        <div className="space-y-2">
          <Label htmlFor="campaign-name">Name</Label>
          <Input
            id="campaign-name"
            value={createForm.name}
            onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="e.g. Affordability Campaign"
            required
            data-testid="campaign-name-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="campaign-program-select">Program</Label>
          <Select value={createForm.programId} onValueChange={(v) => setCreateForm((f) => ({ ...f, programId: v }))}>
            <SelectTrigger id="campaign-program-select" data-testid="campaign-program-select">
              <SelectValue placeholder="Select a Program" />
            </SelectTrigger>
            <SelectContent>
              {programs.map((p) => (
                <SelectItem key={p.program_id} value={p.program_id} data-testid={`campaign-program-option-${p.program_id}`}>
                  {p.name} ({p.program_id})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="campaign-angle-select">Angle (optional)</Label>
          <Select value={createForm.angleId} onValueChange={(v) => setCreateForm((f) => ({ ...f, angleId: v }))}>
            <SelectTrigger id="campaign-angle-select" data-testid="campaign-angle-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_ANGLE}>No Angle yet</SelectItem>
              {angles.map((a) => (
                <SelectItem key={a.angle_id} value={a.angle_id} data-testid={`campaign-angle-option-${a.angle_id}`}>
                  {a.name} ({a.angle_id})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <StatusField value={createForm.status} onChange={(status) => setCreateForm((f) => ({ ...f, status }))} testId="campaign-status-select" />
      </EntityFormDialog>

      {editingCampaign && (
        <EntityFormDialog
          testId="edit-campaign-dialog"
          open={!!editingCampaign}
          onOpenChange={(open) => !open && setEditingCampaign(null)}
          title={`Edit ${editingCampaign.campaign_id}`}
          onSubmit={submitEdit}
          submitting={mutating}
          error={mutationError}
          submitLabel="Save Changes"
        >
          <div className="space-y-2">
            <Label htmlFor="campaign-edit-name">Name</Label>
            <Input
              id="campaign-edit-name"
              value={editForm.name}
              onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
              required
              data-testid="campaign-edit-name-input"
            />
          </div>

          <div className="space-y-2">
            <Label>Program</Label>
            <div className="rounded-md border border-white/10 bg-white/5 px-3 py-2 text-sm text-slate-300" data-testid="campaign-edit-program-readonly">
              {programName(editingCampaign.program_id) || editingCampaign.program_id} ({editingCampaign.program_id})
            </div>
            <p className="text-xs text-slate-500">A Campaign's Program cannot be changed after creation.</p>
          </div>

          <div className="space-y-2">
            <Label>Angle</Label>
            {editingCampaign.angle_id ? (
              <>
                <div className="rounded-md border border-white/10 bg-white/5 px-3 py-2 text-sm text-slate-300" data-testid="campaign-edit-angle-readonly">
                  {angleName(editingCampaign.angle_id) || editingCampaign.angle_id} ({editingCampaign.angle_id})
                </div>
                <p className="text-xs text-slate-500">Once assigned, a Campaign's Angle cannot be reassigned.</p>
              </>
            ) : (
              <>
                <Select value={editForm.angleId} onValueChange={(v) => setEditForm((f) => ({ ...f, angleId: v }))}>
                  <SelectTrigger data-testid="campaign-edit-angle-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_ANGLE}>No Angle yet</SelectItem>
                    {angles.map((a) => (
                      <SelectItem key={a.angle_id} value={a.angle_id} data-testid={`campaign-edit-angle-option-${a.angle_id}`}>
                        {a.name} ({a.angle_id})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">This can be assigned once — it cannot be changed after saving.</p>
              </>
            )}
          </div>

          <StatusField value={editForm.status} onChange={(status) => setEditForm((f) => ({ ...f, status }))} testId="campaign-edit-status-select" />
        </EntityFormDialog>
      )}
    </div>
  );
}

function StatusField({ value, onChange, testId }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={testId}>Status</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger id={testId} data-testid={testId}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {CAMPAIGN_STATUS_VALUES.map((s) => (
            <SelectItem key={s} value={s} data-testid={`${testId}-option-${s}`}>
              {s}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function Detail({ label, value, mono }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className={mono ? "font-mono text-slate-200" : "text-slate-200"}>{value}</div>
    </div>
  );
}
